// Frameworks — AI-first vs Mini-pod comparison (Phase D of
// docs/framework-comparison-plan.md).
//
// One tab that compares the two delivery frameworks HONESTLY, entirely from the
// work_items fact layer (no story points, no sprint ceremony — the same anchors
// for both sides). Every number here is computed the same way for each
// framework. Sources (all session-authenticated, same fetch pattern as the
// other pulse components):
//   GET /api/work-items/summary?weeks=N  → per-(framework,team,mini_pod) rollup
//   GET /api/pulse/ai-evidence?weeks=N    → MR-evidence gate stats + label audit
//   GET /api/baselines                    → pinned per-unit baselines (delta ref)
//
// n=1 honesty: a mini-pod IS one developer, so every mini-pod row/card shows its
// sample_n and is compared only to its OWN pinned baseline — never mini-pod vs
// mini-pod, never a leaderboard. These are health indicators.

import { useState, useEffect, useCallback } from 'react'
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Cell,
    ResponsiveContainer,
} from 'recharts'
import { PULSE_COLORS } from './pulseConfig.js'

const C = PULSE_COLORS

// The two frameworks. Colors are visually distinct AND consistent across every
// panel; a text/glyph tag always accompanies the color so nothing relies on hue
// alone. Data uses framework='jira' for the mini-pod side, 'ai-first' for gogo.
const FW = {
    jira: { key: 'jira', label: 'Mini-pod', tag: '◆', color: C.success, soft: `${C.success}22` },
    'ai-first': { key: 'ai-first', label: 'AI-first', tag: '▲', color: C.primary, soft: `${C.primary}22` },
}
const FW_ORDER = ['ai-first', 'jira']

const WEEK_OPTIONS = [4, 6, 8, 12]

// ── Pure helpers ─────────────────────────────────────────────────────────────

const fmt = (v, digits = 1) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(digits))
const fmtInt = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : String(Math.round(Number(v))))
const fmtHours = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `${Math.round(Number(v))}h`)
// Sub-hour precision for review-turnaround: agent reviews land in minutes
// (e.g. 0.2h), which fmtHours would round away to "0h". Keep one decimal below
// 10h, integer above.
const fmtHoursPrecise = (v) =>
    v == null || !Number.isFinite(Number(v)) ? '—' : `${Number(v) < 10 ? Number(v).toFixed(1) : Math.round(Number(v))}h`
const fmtPct = (frac, digits = 0) =>
    frac == null || !Number.isFinite(Number(frac)) ? '—' : `${(Number(frac) * 100).toFixed(digits)}%`

const unitLabel = (g) => g.mini_pod || g.team || '—'
const unitType = (g) => (g.mini_pod ? 'mini_pod' : 'team')
const unitKey = (g) => g.mini_pod || g.team || ''

// Weighted mean of a numeric field over rows, ignoring null values / weights.
// Falls back to a simple mean of present values when no positive weights exist.
function weightedMean(rows, field, weightField) {
    let wSum = 0
    let wvSum = 0
    let simpleSum = 0
    let simpleN = 0
    for (const r of rows) {
        const v = r[field]
        if (v == null || !Number.isFinite(Number(v))) continue
        const w = Number(r[weightField]) || 0
        simpleSum += Number(v)
        simpleN += 1
        if (w > 0) {
            wSum += w
            wvSum += w * Number(v)
        }
    }
    if (wSum > 0) return wvSum / wSum
    if (simpleN > 0) return simpleSum / simpleN
    return null
}

const normMetric = (m) =>
    String(m ?? '')
        .toLowerCase()
        .replace(/[\s_-]+/g, '')

// metric-name aliases → tolerate whatever string was pinned in framework_baselines.
const BASELINE_ALIASES = {
    throughput: ['throughput', 'throughput_per_week', 'items_per_week', 'items/wk'],
    lead_p50: ['lead_time_p50', 'lead_time_p50_hours', 'lead time', 'leadtime', 'lead_time', 'lead_p50'],
    escape_rate: ['escape_rate', 'escape rate', 'escapes', 'escape'],
}

// Build a (unit_type|unit_key|normMetric) → value lookup from the baselines list.
function buildBaselineLookup(baselines) {
    const map = new Map()
    for (const b of baselines || []) {
        const val = Number(b.value)
        if (!Number.isFinite(val)) continue
        map.set(`${b.unit_type}|${b.unit_key}|${normMetric(b.metric)}`, val)
    }
    return map
}

function unitBaseline(map, g, metricGroup) {
    const ut = unitType(g)
    const uk = unitKey(g)
    if (!uk) return null
    for (const alias of BASELINE_ALIASES[metricGroup] || []) {
        const hit = map.get(`${ut}|${uk}|${normMetric(alias)}`)
        if (hit != null && Number.isFinite(hit)) return hit
    }
    return null
}

// Aggregate the per-unit summary rows of one framework into a framework-level
// view. Throughput sums (framework items/week); lead time is items-weighted; the
// escape rate is escapes/deployed; low-n if ANY constituent unit has n<=1.
function aggregateFramework(groups, fwKey) {
    const units = groups.filter((g) => g.framework === fwKey)
    if (units.length === 0) {
        return { fwKey, unitCount: 0, empty: true, units }
    }
    const itemsDeployed = units.reduce((s, u) => s + (Number(u.items_deployed) || 0), 0)
    const itemsClosed = units.reduce((s, u) => s + (Number(u.items_closed) || 0), 0)
    const escapes = units.reduce((s, u) => s + (Number(u.escapes) || 0), 0)
    const throughputTotal = units.reduce((s, u) => s + (Number(u.throughput_per_week) || 0), 0)
    return {
        fwKey,
        unitCount: units.length,
        units,
        empty: false,
        itemsDeployed,
        itemsClosed,
        escapes,
        throughputTotal,
        throughputPerUnit: throughputTotal / units.length,
        leadP50: weightedMean(units, 'lead_time_p50_hours', 'items_deployed'),
        leadP85: weightedMean(units, 'lead_time_p85_hours', 'items_deployed'),
        pctAgent: weightedMean(units, 'pct_agent_authored', 'items_deployed'),
        escapeRate: itemsDeployed > 0 ? escapes / itemsDeployed : null,
        anyLowN: units.some((u) => (Number(u.sample_n) || 0) <= 1),
        // Flow decomposition. Lead time alone cannot tell "we got faster" from
        // "work stopped waiting" — queue is the backlog wait, work is hands-on
        // time. Weighted by exited_n, the denominator these medians were taken
        // over (NOT items_deployed, which counts a different population).
        queueP50: weightedMean(units, 'queue_p50_hours', 'exited_n'),
        workP50: weightedMean(units, 'work_p50_hours', 'exited_n'),
        flowEfficiency: weightedMean(units, 'flow_efficiency', 'exited_n'),
        // Share of exited items that recorded a start transition. Everything above
        // describes only those, so a low value invalidates the flow read.
        startedCoverage: weightedMean(units, 'started_coverage', 'exited_n'),
    }
}

// Framework-level baseline for a metric group: only returned when EVERY unit has
// a pinned baseline (partial coverage → null, so we never imply a delta we can't
// honestly compute). throughput sums; lead/escape are simple means.
function frameworkBaseline(units, map, metricGroup) {
    const vals = units.map((u) => unitBaseline(map, u, metricGroup))
    if (vals.length === 0 || vals.some((v) => v == null)) return null
    if (metricGroup === 'throughput') {
        const total = vals.reduce((s, v) => s + v, 0)
        return { total, perUnit: total / vals.length }
    }
    const mean = vals.reduce((s, v) => s + v, 0) / vals.length
    return { mean }
}

// Aggregate the per-unit weekly series (GET /api/work-items/weekly) into
// framework-level rows for the trend charts: per ISO week, total throughput and
// total WIP for each framework. WIP sums only over units that report it (all-null
// week → null, so the line breaks rather than dropping to a fake 0). Kanban has
// no sprint bar, so this weekly cadence is how the mini-pod side is read.
function frameworkWeeklyTrend(weekly) {
    if (!weekly?.week_axis?.length) return []
    const axis = weekly.week_axis
    const idx = new Map(axis.map((w, i) => [w, i]))
    return axis.map((wk) => {
        const row = { week: wk }
        for (const fw of FW_ORDER) {
            let thru = 0
            let wip = 0
            let wipSeen = false
            for (const u of weekly.units || []) {
                if (u.framework !== fw) continue
                const p = u.points?.[idx.get(wk)]
                if (!p) continue
                thru += Number(p.throughput) || 0
                if (p.wip != null) {
                    wip += Number(p.wip) || 0
                    wipSeen = true
                }
            }
            row[`${fw}_thru`] = thru
            row[`${fw}_wip`] = wipSeen ? wip : null
        }
        return row
    })
}

// 'YYYY-MM-DD' → 'M/D' for compact week-axis ticks.
const weekTick = (w) => {
    const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(String(w))
    return m ? `${Number(m[1])}/${Number(m[2])}` : w
}

// ── Small presentational pieces ──────────────────────────────────────────────

function FwTag({ fwKey, muted }) {
    const f = FW[fwKey]
    if (!f) return null
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600 }}>
            <span style={{ color: f.color, fontSize: '12px' }}>{f.tag}</span>
            <span style={{ color: muted ? C.textMuted : C.text }}>{f.label}</span>
        </span>
    )
}

function NBadge({ n }) {
    return (
        <span
            style={{
                display: 'inline-block',
                fontSize: '9px',
                fontWeight: 700,
                letterSpacing: '0.4px',
                border: `1px solid ${C.warning}66`,
                color: C.warning,
                borderRadius: '4px',
                padding: '0 5px',
                marginLeft: '6px',
                verticalAlign: '1px',
            }}
            title="Small sample — at least one aggregated unit has n≤1"
        >
            n={n}
        </span>
    )
}

// A delta chip vs a baseline value. dir='up' means higher is better (throughput);
// 'down' means lower is better (lead time, escape rate).
function Delta({ current, baseline, dir = 'up', unit = '' }) {
    if (current == null || baseline == null || !Number.isFinite(baseline) || baseline === 0) return null
    const pctChange = ((current - baseline) / Math.abs(baseline)) * 100
    const improved = dir === 'up' ? pctChange > 3 : pctChange < -3
    const worse = dir === 'up' ? pctChange < -3 : pctChange > 3
    const color = improved ? C.success : worse ? C.danger : C.textMuted
    const arrow = pctChange > 3 ? '↑' : pctChange < -3 ? '↓' : '≈'
    return (
        <div style={{ fontSize: '11px', fontWeight: 600, color, marginTop: '3px' }}>
            {arrow} {Math.abs(pctChange).toFixed(0)}% vs baseline {fmt(baseline, dir === 'up' ? 1 : 0)}
            {unit}
        </div>
    )
}

// One verdict tile showing both frameworks side by side.
function VerdictTile({ label, badge, children }) {
    return (
        <div
            style={{
                background: C.cardBg,
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                padding: '14px 16px',
            }}
        >
            <div
                style={{
                    fontSize: '11px',
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: C.textMuted,
                    fontWeight: 600,
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                {label}
                {badge}
            </div>
            <div style={{ display: 'flex', gap: '20px' }}>{children}</div>
        </div>
    )
}

function TileSide({ fwKey, value, sub, delta, lowN }) {
    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                <FwTag fwKey={fwKey} muted />
                {lowN && <NBadge n={1} />}
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: C.text, letterSpacing: '-0.01em' }}>{value}</div>
            {sub && <div style={{ fontSize: '11px', color: C.textSubtle, marginTop: '1px' }}>{sub}</div>}
            {delta}
        </div>
    )
}

// ── Main component ───────────────────────────────────────────────────────────

export default function Frameworks() {
    const [weeks, setWeeks] = useState(6)
    const [summary, setSummary] = useState(null) // { weeks, groups }
    const [weekly, setWeekly] = useState(null) // { weeks, week_axis, units }
    const [evidence, setEvidence] = useState(null) // { by_pod, label_audit }
    const [baselines, setBaselines] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [notConfigured, setNotConfigured] = useState(false)
    const [sort, setSort] = useState({ key: 'throughput_per_week', dir: 'desc' })

    const loadAll = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const [sumRes, wkRes, evRes, blRes] = await Promise.all([
                fetch(`/api/work-items/summary?weeks=${weeks}`, { credentials: 'include' }),
                fetch(`/api/work-items/weekly?weeks=${weeks}`, { credentials: 'include' }),
                fetch(`/api/pulse/ai-evidence?weeks=${weeks}`, { credentials: 'include' }),
                fetch('/api/baselines', { credentials: 'include' }),
            ])

            // 503 from the primary source = no database configured for this env.
            if (sumRes.status === 503) {
                setNotConfigured(true)
                setSummary(null)
                setWeekly(null)
                setEvidence(null)
                setBaselines([])
                return
            }
            if (sumRes.status === 401 || evRes.status === 401 || blRes.status === 401) {
                throw new Error('Session expired — please refresh to sign in again.')
            }
            if (!sumRes.ok) throw new Error(`work-items summary failed (${sumRes.status})`)

            const sumData = await sumRes.json()
            setNotConfigured(false)
            setSummary(sumData)

            // Weekly trend is best-effort: a failure degrades the trend panel only.
            if (wkRes.ok) {
                setWeekly(await wkRes.json())
            } else {
                setWeekly(null)
            }

            // Evidence + baselines are best-effort: a failure there degrades the
            // gate/baseline panels but must not blank the whole comparison.
            if (evRes.ok) {
                setEvidence(await evRes.json())
            } else {
                setEvidence(null)
            }
            if (blRes.ok) {
                const blData = await blRes.json()
                setBaselines(Array.isArray(blData) ? blData : blData.baselines || [])
            } else {
                setBaselines([])
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [weeks])

    useEffect(() => {
        loadAll()
    }, [loadAll])

    const groups = summary?.groups || []
    const blMap = buildBaselineLookup(baselines)
    const aiAgg = aggregateFramework(groups, 'ai-first')
    const mpAgg = aggregateFramework(groups, 'jira')
    const aiFirstEmpty = aiAgg.empty
    const podEvidence = evidence?.by_pod || []
    const labelAudit = evidence?.label_audit || null

    // Overall gate rollup from ai-evidence (mini-pod / Jira MR side). MRs-weighted
    // where a weighted mean is meaningful; medians can't be truly pooled so the
    // turnaround is shown as an MRs-weighted approximation and marked "≈".
    const gate = (() => {
        if (podEvidence.length === 0) return null
        const totalMrs = podEvidence.reduce((s, p) => s + (Number(p.mrs_total) || 0), 0)
        const reviewedHuman = podEvidence.reduce((s, p) => s + (Number(p.reviewed_by?.human) || 0), 0)
        const reviewedAgent = podEvidence.reduce((s, p) => s + (Number(p.reviewed_by?.agent) || 0), 0)
        // Per-type medians exist per pod, but pooling them across pods is still
        // an approximation (medians don't sum) — the overall tile keeps its "≈".
        // Weight each pod's per-type median by that pod's reviewed-of-that-type
        // count. The exact per-type medians surface in the per-pod panel below.
        const hasByType = podEvidence.some((p) => p.review_turnaround_by_type != null)
        const byTypeRows = podEvidence.map((p) => ({
            human: p.review_turnaround_by_type?.human ?? null,
            agent: p.review_turnaround_by_type?.agent ?? null,
            wHuman: Number(p.reviewed_by?.human) || 0,
            wAgent: Number(p.reviewed_by?.agent) || 0,
        }))
        return {
            totalMrs,
            reviewedHuman,
            reviewedAgent,
            hasByType,
            firstPass: weightedMean(podEvidence, 'first_pass_approval_rate', 'mrs_total'),
            turnaround: weightedMean(podEvidence, 'median_review_turnaround_hours', 'mrs_total'),
            turnaroundHuman: weightedMean(byTypeRows, 'human', 'wHuman'),
            turnaroundAgent: weightedMean(byTypeRows, 'agent', 'wAgent'),
            blocking: weightedMean(podEvidence, 'blocking_findings_per_mr', 'mrs_total'),
        }
    })()

    // ── Styles ───────────────────────────────────────────────────────────────
    const cardStyle = { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px' }
    const panelStyle = { ...cardStyle }
    const th = {
        textAlign: 'right',
        padding: '7px 10px',
        color: C.textMuted,
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        borderBottom: `1px solid ${C.border}`,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
    }
    const thLeft = { ...th, textAlign: 'left' }
    const td = {
        padding: '6px 10px',
        borderBottom: `1px solid ${C.border}`,
        fontSize: '12.5px',
        color: C.text,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
    }
    const tdLeft = { ...td, textAlign: 'left' }

    // ── Empty / loading / error / not-configured states ────────────────────────
    if (notConfigured) {
        return (
            <div style={cardStyle}>
                <div style={{ color: C.textMuted, fontSize: '13px', padding: '30px 0', textAlign: 'center' }}>
                    The work_items store isn&apos;t available — no database is configured for this environment.
                </div>
            </div>
        )
    }

    // Sorted rows for the per-unit table.
    const sortedRows = [...groups].sort((a, b) => {
        const dir = sort.dir === 'asc' ? 1 : -1
        let av
        let bv
        if (sort.key === 'framework') {
            av = FW[a.framework]?.label || ''
            bv = FW[b.framework]?.label || ''
            return av.localeCompare(bv) * dir
        }
        if (sort.key === 'unit') {
            return unitLabel(a).localeCompare(unitLabel(b)) * dir
        }
        av = Number(a[sort.key])
        bv = Number(b[sort.key])
        if (!Number.isFinite(av)) av = -Infinity
        if (!Number.isFinite(bv)) bv = -Infinity
        return (av - bv) * dir
    })

    const setSortKey = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))
    const sortArrow = (key) => (sort.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '')

    // Lead-time chart data (only units with a p50).
    const leadData = groups
        .filter((g) => g.lead_time_p50_hours != null)
        .map((g) => ({
            name: `${FW[g.framework]?.tag || ''} ${unitLabel(g)}`,
            p50: Number(g.lead_time_p50_hours),
            p85: g.lead_time_p85_hours != null ? Number(g.lead_time_p85_hours) : null,
            color: FW[g.framework]?.color || C.textMuted,
            framework: g.framework,
        }))

    // Weekly flow trend (Kanban cadence) — framework-level throughput + WIP series.
    const trend = frameworkWeeklyTrend(weekly)
    const hasTrend = trend.length > 0
    const wipReported = trend.some((r) => r.jira_wip != null || r['ai-first_wip'] != null)

    // Delta helpers for the verdict tiles (framework-level, complete-coverage only).
    const aiThruBl = aiAgg.empty ? null : frameworkBaseline(aiAgg.units, blMap, 'throughput')
    const mpThruBl = mpAgg.empty ? null : frameworkBaseline(mpAgg.units, blMap, 'throughput')
    const aiLeadBl = aiAgg.empty ? null : frameworkBaseline(aiAgg.units, blMap, 'lead_p50')
    const mpLeadBl = mpAgg.empty ? null : frameworkBaseline(mpAgg.units, blMap, 'lead_p50')

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* ── Header ── */}
            <div style={cardStyle}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '16px',
                        flexWrap: 'wrap',
                    }}
                >
                    <div>
                        <h3 style={{ color: C.text, fontWeight: 600, fontSize: '18px', margin: 0 }}>
                            ⚖️ AI-first vs Mini-pod
                        </h3>
                        <p style={{ color: C.textMuted, fontSize: '13px', margin: '6px 0 0', maxWidth: '640px' }}>
                            Both sides computed the same way from the <code>work_items</code> fact layer — no story
                            points, no self-reported labels. Each unit is compared to its OWN pinned baseline; mini-pods
                            are n=1 (one developer), so treat them as health indicators, not a leaderboard.
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '12px', color: C.textMuted }}>Window</span>
                        <div
                            style={{
                                display: 'inline-flex',
                                background: C.bgInner,
                                border: `1px solid ${C.border}`,
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}
                        >
                            {WEEK_OPTIONS.map((w) => (
                                <button
                                    key={w}
                                    onClick={() => setWeeks(w)}
                                    style={{
                                        background: weeks === w ? C.primary : 'transparent',
                                        border: 'none',
                                        color: weeks === w ? '#fff' : C.textMuted,
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        fontWeight: weeks === w ? 700 : 400,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {w}w
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={loadAll}
                            disabled={loading}
                            style={{
                                background: 'transparent',
                                border: `1px solid ${C.border}`,
                                borderRadius: '8px',
                                color: C.textMuted,
                                padding: '6px 12px',
                                fontSize: '12px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {loading ? '…' : '↻'}
                        </button>
                    </div>
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '18px', marginTop: '14px', flexWrap: 'wrap' }}>
                    {FW_ORDER.map((k) => (
                        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                            <span
                                style={{ width: '11px', height: '11px', borderRadius: '3px', background: FW[k].color }}
                            />
                            <FwTag fwKey={k} muted />
                        </span>
                    ))}
                </div>

                {/* Data-state banner */}
                {error && (
                    <div
                        style={{
                            background: `${C.danger}14`,
                            border: `1px solid ${C.danger}55`,
                            borderRadius: '8px',
                            color: C.danger,
                            padding: '9px 12px',
                            fontSize: '12px',
                            marginTop: '14px',
                        }}
                    >
                        {error}
                    </div>
                )}
                {!error && !loading && summary && (
                    <div
                        style={{
                            marginTop: '14px',
                            fontSize: '12px',
                            color: C.textMuted,
                            display: 'flex',
                            gap: '20px',
                            flexWrap: 'wrap',
                        }}
                    >
                        <span>
                            <FwTag fwKey="jira" muted />
                            {'  '}
                            {mpAgg.empty
                                ? ' — no synced work items'
                                : ` — ${mpAgg.unitCount} unit${mpAgg.unitCount === 1 ? '' : 's'}, ${mpAgg.itemsDeployed} deployed / ${mpAgg.itemsClosed} closed in ${weeks}w`}
                        </span>
                        <span>
                            <FwTag fwKey="ai-first" muted />
                            {'  '}
                            {aiAgg.empty
                                ? ' — no synced work items'
                                : ` — ${aiAgg.unitCount} unit${aiAgg.unitCount === 1 ? '' : 's'}, ${aiAgg.itemsDeployed} deployed / ${aiAgg.itemsClosed} closed in ${weeks}w`}
                        </span>
                    </div>
                )}
                {!error && !loading && aiFirstEmpty && summary && (
                    <div
                        style={{
                            marginTop: '10px',
                            background: `${C.warning}14`,
                            border: `1px solid ${C.warning}55`,
                            borderRadius: '8px',
                            color: C.warning,
                            padding: '10px 12px',
                            fontSize: '12.5px',
                        }}
                    >
                        <strong>AI-first side has no synced work items yet.</strong> Run the{' '}
                        <code>sync-work-items</code> job (POST <code>/api/jobs/sync-work-items</code>) to ingest the
                        argus bug-queue. The mini-pod columns below are real; the AI-first columns will populate once
                        the sync runs.
                    </div>
                )}
            </div>

            {loading && !summary ? (
                <div style={cardStyle}>
                    <div style={{ color: C.textMuted, fontSize: '13px', padding: '30px 0', textAlign: 'center' }}>
                        Loading framework comparison…
                    </div>
                </div>
            ) : !summary ? null : groups.length === 0 ? (
                <div style={cardStyle}>
                    <div style={{ color: C.textMuted, fontSize: '13px', padding: '30px 0', textAlign: 'center' }}>
                        No work items in the last {weeks} weeks. Run the sync-work-items job to populate the fact layer.
                    </div>
                </div>
            ) : (
                <>
                    {/* ── Verdict tiles ── */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '12px',
                        }}
                    >
                        <VerdictTile label="Throughput · items/wk per unit">
                            <TileSide
                                fwKey="ai-first"
                                value={aiAgg.empty ? '—' : fmt(aiAgg.throughputPerUnit, 1)}
                                sub={aiAgg.empty ? 'no data' : `${fmt(aiAgg.throughputTotal, 1)}/wk total`}
                                lowN={!aiAgg.empty && aiAgg.anyLowN}
                                delta={
                                    aiThruBl && (
                                        <Delta current={aiAgg.throughputPerUnit} baseline={aiThruBl.perUnit} dir="up" />
                                    )
                                }
                            />
                            <TileSide
                                fwKey="jira"
                                value={mpAgg.empty ? '—' : fmt(mpAgg.throughputPerUnit, 1)}
                                sub={mpAgg.empty ? 'no data' : `${fmt(mpAgg.throughputTotal, 1)}/wk total`}
                                lowN={!mpAgg.empty && mpAgg.anyLowN}
                                delta={
                                    mpThruBl && (
                                        <Delta current={mpAgg.throughputPerUnit} baseline={mpThruBl.perUnit} dir="up" />
                                    )
                                }
                            />
                        </VerdictTile>

                        <VerdictTile label="Lead time · created → prod, p50">
                            <TileSide
                                fwKey="ai-first"
                                value={aiAgg.empty ? '—' : fmtHours(aiAgg.leadP50)}
                                sub={aiAgg.empty ? 'no data' : `p85 ${fmtHours(aiAgg.leadP85)}`}
                                lowN={!aiAgg.empty && aiAgg.anyLowN}
                                delta={
                                    aiLeadBl && (
                                        <Delta current={aiAgg.leadP50} baseline={aiLeadBl.mean} dir="down" unit="h" />
                                    )
                                }
                            />
                            <TileSide
                                fwKey="jira"
                                value={mpAgg.empty ? '—' : fmtHours(mpAgg.leadP50)}
                                sub={mpAgg.empty ? 'no data' : `p85 ${fmtHours(mpAgg.leadP85)}`}
                                lowN={!mpAgg.empty && mpAgg.anyLowN}
                                delta={
                                    mpLeadBl && (
                                        <Delta current={mpAgg.leadP50} baseline={mpLeadBl.mean} dir="down" unit="h" />
                                    )
                                }
                            />
                        </VerdictTile>

                        <VerdictTile label="Flow · queue vs work, p50">
                            <TileSide
                                fwKey="ai-first"
                                value={
                                    aiAgg.empty || aiAgg.flowEfficiency == null ? '—' : fmtPct(aiAgg.flowEfficiency, 0)
                                }
                                sub={
                                    aiAgg.empty || aiAgg.queueP50 == null
                                        ? 'no start data'
                                        : `queue ${fmtHours(aiAgg.queueP50)} · work ${fmtHours(aiAgg.workP50)}${
                                              aiAgg.startedCoverage != null && aiAgg.startedCoverage < 0.8
                                                  ? ` · only ${fmtPct(aiAgg.startedCoverage, 0)} have a start`
                                                  : ''
                                          }`
                                }
                                lowN={!aiAgg.empty && aiAgg.anyLowN}
                            />
                            <TileSide
                                fwKey="jira"
                                value={
                                    mpAgg.empty || mpAgg.flowEfficiency == null ? '—' : fmtPct(mpAgg.flowEfficiency, 0)
                                }
                                sub={
                                    mpAgg.empty || mpAgg.queueP50 == null
                                        ? 'no start data'
                                        : `queue ${fmtHours(mpAgg.queueP50)} · work ${fmtHours(mpAgg.workP50)}${
                                              mpAgg.startedCoverage != null && mpAgg.startedCoverage < 0.8
                                                  ? ` · only ${fmtPct(mpAgg.startedCoverage, 0)} have a start`
                                                  : ''
                                          }`
                                }
                                lowN={!mpAgg.empty && mpAgg.anyLowN}
                            />
                        </VerdictTile>

                        <VerdictTile label="Escapes · prod bugs / deployed change">
                            <TileSide
                                fwKey="ai-first"
                                value={aiAgg.empty ? '—' : fmtPct(aiAgg.escapeRate, 1)}
                                sub={
                                    aiAgg.empty ? 'no data' : `${aiAgg.escapes} escape${aiAgg.escapes === 1 ? '' : 's'}`
                                }
                                lowN={!aiAgg.empty && aiAgg.anyLowN}
                            />
                            <TileSide
                                fwKey="jira"
                                value={mpAgg.empty ? '—' : fmtPct(mpAgg.escapeRate, 1)}
                                sub={
                                    mpAgg.empty ? 'no data' : `${mpAgg.escapes} escape${mpAgg.escapes === 1 ? '' : 's'}`
                                }
                                lowN={!mpAgg.empty && mpAgg.anyLowN}
                            />
                        </VerdictTile>

                        <VerdictTile label="The review gate">
                            {gate ? (
                                <>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '2px' }}>
                                            First-pass approval
                                        </div>
                                        <div style={{ fontSize: '24px', fontWeight: 700, color: C.text }}>
                                            {fmt(gate.firstPass, 0)}%
                                        </div>
                                        <div style={{ fontSize: '11px', color: C.textSubtle, marginTop: '1px' }}>
                                            {gate.totalMrs} MRs in {weeks}w
                                        </div>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '2px' }}>
                                            Median turnaround ≈
                                        </div>
                                        {gate.hasByType ? (
                                            <>
                                                <div style={{ fontSize: '20px', fontWeight: 700, color: C.text }}>
                                                    human {fmtHoursPrecise(gate.turnaroundHuman)} · agent{' '}
                                                    {fmtHoursPrecise(gate.turnaroundAgent)}
                                                </div>
                                                <div
                                                    style={{ fontSize: '11px', color: C.textSubtle, marginTop: '1px' }}
                                                >
                                                    pooled across pods · {gate.reviewedHuman} human ·{' '}
                                                    {gate.reviewedAgent} agent reviews
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: '24px', fontWeight: 700, color: C.text }}>
                                                    {fmtHours(gate.turnaround)}
                                                </div>
                                                <div
                                                    style={{ fontSize: '11px', color: C.textSubtle, marginTop: '1px' }}
                                                >
                                                    reviewer mix: {gate.reviewedHuman} human · {gate.reviewedAgent}{' '}
                                                    agent
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div style={{ fontSize: '12px', color: C.textSubtle, padding: '4px 0' }}>
                                    No MR gate evidence in this window.
                                </div>
                            )}
                        </VerdictTile>
                    </div>

                    {/* ── Weekly flow trend (Kanban cadence) ── */}
                    <div style={panelStyle}>
                        <h4 style={{ margin: '0 0 2px', color: C.text, fontSize: '15px', fontWeight: 700 }}>
                            Weekly flow trend
                        </h4>
                        <p style={{ margin: '0 0 12px', color: C.textMuted, fontSize: '12.5px' }}>
                            Mini-pods run Kanban — no sprints — so flow is read per ISO calendar week. Throughput is
                            items whose exit (deploy/close) landed that week; WIP is in-progress items at week-end.
                        </p>
                        {!hasTrend ? (
                            <div style={{ color: C.textSubtle, fontSize: '12.5px', padding: '18px 0' }}>
                                No weekly data yet.
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                                    gap: '16px',
                                }}
                            >
                                {/* Throughput per week */}
                                <div>
                                    <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '6px' }}>
                                        Throughput · items exited / week
                                    </div>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <LineChart data={trend} margin={{ left: 4, right: 12, top: 6, bottom: 4 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                                            <XAxis
                                                dataKey="week"
                                                tickFormatter={weekTick}
                                                tick={{ fill: C.textMuted, fontSize: 11 }}
                                                stroke={C.border}
                                            />
                                            <YAxis
                                                tick={{ fill: C.textMuted, fontSize: 11 }}
                                                stroke={C.border}
                                                allowDecimals={false}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    background: C.bgInner,
                                                    border: `1px solid ${C.border}`,
                                                    borderRadius: '8px',
                                                    color: C.text,
                                                    fontSize: '12px',
                                                }}
                                                labelFormatter={(l) => `Week of ${l}`}
                                            />
                                            <Legend wrapperStyle={{ fontSize: '11px' }} />
                                            <Line
                                                type="monotone"
                                                dataKey="ai-first_thru"
                                                name="AI-first"
                                                stroke={FW['ai-first'].color}
                                                strokeWidth={2}
                                                dot={{ r: 2 }}
                                                connectNulls
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="jira_thru"
                                                name="Mini-pod"
                                                stroke={FW.jira.color}
                                                strokeWidth={2}
                                                dot={{ r: 2 }}
                                                connectNulls
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* WIP per week */}
                                <div>
                                    <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '6px' }}>
                                        WIP · in-progress at week-end
                                    </div>
                                    {!wipReported ? (
                                        <div
                                            style={{
                                                color: C.textSubtle,
                                                fontSize: '12px',
                                                padding: '18px 0',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            No WIP captured yet. AI-first WIP derives from bug-queue intervals; Jira
                                            mini-pod WIP accrues forward from the daily in-progress capture (
                                            <code>wip_facts</code>) once the snapshot job has run.
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={220}>
                                            <LineChart data={trend} margin={{ left: 4, right: 12, top: 6, bottom: 4 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                                                <XAxis
                                                    dataKey="week"
                                                    tickFormatter={weekTick}
                                                    tick={{ fill: C.textMuted, fontSize: 11 }}
                                                    stroke={C.border}
                                                />
                                                <YAxis
                                                    tick={{ fill: C.textMuted, fontSize: 11 }}
                                                    stroke={C.border}
                                                    allowDecimals={false}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        background: C.bgInner,
                                                        border: `1px solid ${C.border}`,
                                                        borderRadius: '8px',
                                                        color: C.text,
                                                        fontSize: '12px',
                                                    }}
                                                    labelFormatter={(l) => `Week of ${l}`}
                                                />
                                                <Legend wrapperStyle={{ fontSize: '11px' }} />
                                                <Line
                                                    type="monotone"
                                                    dataKey="ai-first_wip"
                                                    name="AI-first"
                                                    stroke={FW['ai-first'].color}
                                                    strokeWidth={2}
                                                    dot={{ r: 2 }}
                                                    connectNulls
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="jira_wip"
                                                    name="Mini-pod"
                                                    stroke={FW.jira.color}
                                                    strokeWidth={2}
                                                    dot={{ r: 2 }}
                                                    connectNulls
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Lead-time bar comparison (Recharts) ── */}
                    <div style={panelStyle}>
                        <h4 style={{ margin: '0 0 2px', color: C.text, fontSize: '15px', fontWeight: 700 }}>
                            Lead time by unit
                        </h4>
                        <p style={{ margin: '0 0 12px', color: C.textMuted, fontSize: '12.5px' }}>
                            created → production, p50 (solid) and p85 (faded), colored by framework
                        </p>
                        {leadData.length === 0 ? (
                            <div style={{ color: C.textSubtle, fontSize: '12.5px', padding: '18px 0' }}>
                                No lead-time samples yet (needs items with both a created and a deployed timestamp).
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={Math.max(220, leadData.length * 42)}>
                                <BarChart
                                    data={leadData}
                                    layout="vertical"
                                    margin={{ left: 20, right: 24, top: 4, bottom: 4 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                                    <XAxis
                                        type="number"
                                        tick={{ fill: C.textMuted, fontSize: 11 }}
                                        stroke={C.border}
                                        tickFormatter={(v) => `${v}h`}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        width={140}
                                        tick={{ fill: C.textMuted, fontSize: 11 }}
                                        stroke={C.border}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: C.bgInner,
                                            border: `1px solid ${C.border}`,
                                            borderRadius: '8px',
                                            color: C.text,
                                            fontSize: '12px',
                                        }}
                                        formatter={(v, n) => [v == null ? '—' : `${Math.round(v)}h`, n]}
                                    />
                                    <Bar dataKey="p85" name="p85" radius={[0, 3, 3, 0]}>
                                        {leadData.map((d, i) => (
                                            <Cell key={`p85-${i}`} fill={d.color} fillOpacity={0.28} />
                                        ))}
                                    </Bar>
                                    <Bar dataKey="p50" name="p50" radius={[0, 3, 3, 0]}>
                                        {leadData.map((d, i) => (
                                            <Cell key={`p50-${i}`} fill={d.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* ── Per-unit table ── */}
                    <div style={panelStyle}>
                        <h4 style={{ margin: '0 0 2px', color: C.text, fontSize: '15px', fontWeight: 700 }}>
                            Per team / mini-pod
                        </h4>
                        <p style={{ margin: '0 0 12px', color: C.textMuted, fontSize: '12.5px' }}>
                            Click a column to sort. Each mini-pod is one developer — read <code>n</code> before any row.
                        </p>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                                <thead>
                                    <tr>
                                        <th style={thLeft} onClick={() => setSortKey('framework')}>
                                            Framework{sortArrow('framework')}
                                        </th>
                                        <th style={thLeft} onClick={() => setSortKey('unit')}>
                                            Unit{sortArrow('unit')}
                                        </th>
                                        <th style={th} onClick={() => setSortKey('throughput_per_week')}>
                                            Thru/wk{sortArrow('throughput_per_week')}
                                        </th>
                                        <th style={th} onClick={() => setSortKey('lead_time_p50_hours')}>
                                            Lead p50{sortArrow('lead_time_p50_hours')}
                                        </th>
                                        <th style={th} onClick={() => setSortKey('lead_time_p85_hours')}>
                                            Lead p85{sortArrow('lead_time_p85_hours')}
                                        </th>
                                        <th style={th} onClick={() => setSortKey('pct_agent_authored')}>
                                            % agent{sortArrow('pct_agent_authored')}
                                        </th>
                                        <th style={th} onClick={() => setSortKey('escapes')}>
                                            Escapes{sortArrow('escapes')}
                                        </th>
                                        <th style={th} onClick={() => setSortKey('sample_n')}>
                                            n{sortArrow('sample_n')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map((g, i) => {
                                        const lowN = (Number(g.sample_n) || 0) <= 1
                                        return (
                                            <tr key={`${g.framework}-${unitKey(g)}-${i}`}>
                                                <td style={tdLeft}>
                                                    <FwTag fwKey={g.framework} />
                                                </td>
                                                <td style={{ ...tdLeft, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                                                    {unitLabel(g)}
                                                </td>
                                                <td style={td}>{fmt(g.throughput_per_week, 2)}</td>
                                                <td style={td}>{fmtHours(g.lead_time_p50_hours)}</td>
                                                <td style={td}>{fmtHours(g.lead_time_p85_hours)}</td>
                                                <td style={td}>{fmtPct(g.pct_agent_authored, 0)}</td>
                                                <td style={td}>{fmtInt(g.escapes)}</td>
                                                <td style={{ ...td, color: lowN ? C.warning : C.text }}>
                                                    {fmtInt(g.sample_n)}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── Gate panel (ai-evidence) ── */}
                    <div style={panelStyle}>
                        <h4 style={{ margin: '0 0 2px', color: C.text, fontSize: '15px', fontWeight: 700 }}>
                            Review gate &amp; label audit
                        </h4>
                        <p style={{ margin: '0 0 12px', color: C.textMuted, fontSize: '12.5px' }}>
                            MR-evidence per Jira pod — reviewer-agnostic gate stats plus the AICompleted
                            label-compliance audit that makes the legacy AI% interpretable.
                        </p>
                        {podEvidence.length === 0 ? (
                            <div style={{ color: C.textSubtle, fontSize: '12.5px', padding: '18px 0' }}>
                                No MR evidence in this window (needs synced release_mrs with Phase-B evidence columns).
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                                    <thead>
                                        <tr>
                                            <th style={thLeft}>Pod</th>
                                            <th style={th}>MRs</th>
                                            <th style={th}>% agent</th>
                                            <th style={th}>First-pass</th>
                                            <th style={th}>Turnaround p50</th>
                                            <th style={th}>Block/MR</th>
                                            <th style={th}>Reviewer h/a</th>
                                            <th style={th}>Label ✓</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {podEvidence.map((p) => (
                                            <tr key={p.pod}>
                                                <td style={{ ...tdLeft, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                                                    {p.pod}
                                                </td>
                                                <td style={td}>{fmtInt(p.mrs_total)}</td>
                                                <td style={td}>{fmt(p.pct_agent, 0)}%</td>
                                                <td style={td}>
                                                    {p.first_pass_approval_rate == null
                                                        ? '—'
                                                        : `${fmt(p.first_pass_approval_rate, 0)}%`}
                                                </td>
                                                <td style={td}>
                                                    {p.review_turnaround_by_type ? (
                                                        <span title="True per-reviewer-type medians (exact per pod — not pooled)">
                                                            h {fmtHoursPrecise(p.review_turnaround_by_type.human)} · a{' '}
                                                            {fmtHoursPrecise(p.review_turnaround_by_type.agent)}
                                                        </span>
                                                    ) : (
                                                        fmtHours(p.median_review_turnaround_hours)
                                                    )}
                                                </td>
                                                <td style={td}>{fmt(p.blocking_findings_per_mr, 1)}</td>
                                                <td style={td}>
                                                    {Number(p.reviewed_by?.human) || 0}/
                                                    {Number(p.reviewed_by?.agent) || 0}
                                                </td>
                                                <td style={td}>
                                                    {p.label_audit?.label_compliance_pct == null
                                                        ? '—'
                                                        : `${fmt(p.label_audit.label_compliance_pct, 0)}%`}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {labelAudit && (
                                    <div style={{ marginTop: '10px', fontSize: '12px', color: C.textMuted }}>
                                        Overall label compliance:{' '}
                                        <strong style={{ color: C.text }}>
                                            {labelAudit.label_compliance_pct == null
                                                ? '—'
                                                : `${fmt(labelAudit.label_compliance_pct, 0)}%`}
                                        </strong>{' '}
                                        ({labelAudit.of_those_labeled_AICompleted}/{labelAudit.issues_with_ai_evidence}{' '}
                                        AI-evidenced issues carry the label · {labelAudit.labeled_without_evidence}{' '}
                                        labeled without evidence)
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Per mini-pod strip ── */}
                    <div style={panelStyle}>
                        <h4 style={{ margin: '0 0 2px', color: C.text, fontSize: '15px', fontWeight: 700 }}>
                            Per mini-pod
                        </h4>
                        <p style={{ margin: '0 0 12px', color: C.textMuted, fontSize: '12.5px' }}>
                            Each card is one delivery unit vs its own pinned baseline — rolling {weeks}-week window. Not
                            a leaderboard.
                        </p>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                gap: '10px',
                            }}
                        >
                            {groups.map((g, i) => {
                                const thruBl = unitBaseline(blMap, g, 'throughput')
                                const lowN = (Number(g.sample_n) || 0) <= 1
                                return (
                                    <div
                                        key={`${g.framework}-${unitKey(g)}-${i}`}
                                        style={{
                                            background: C.bgInner,
                                            border: `1px solid ${C.border}`,
                                            borderRadius: '10px',
                                            padding: '11px 13px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '6px',
                                            }}
                                        >
                                            <FwTag fwKey={g.framework} />
                                            {lowN && <NBadge n={Number(g.sample_n) || 0} />}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                fontFamily: 'ui-monospace, Menlo, monospace',
                                                color: C.text,
                                                marginTop: '5px',
                                            }}
                                        >
                                            {unitLabel(g)}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '20px',
                                                fontWeight: 700,
                                                color: C.text,
                                                marginTop: '3px',
                                            }}
                                        >
                                            {fmt(g.throughput_per_week, 1)}{' '}
                                            <span style={{ fontSize: '11px', color: C.textSubtle, fontWeight: 500 }}>
                                                items/wk
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '3px' }}>
                                            lead p50 {fmtHours(g.lead_time_p50_hours)} · {fmtInt(g.escapes)} esc
                                        </div>
                                        {thruBl != null && (
                                            <Delta current={g.throughput_per_week} baseline={thruBl} dir="up" />
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* ── Footnote ── */}
                    <p style={{ fontSize: '12px', color: C.textSubtle, maxWidth: '80ch', lineHeight: 1.55 }}>
                        <strong style={{ color: C.textMuted }}>How to read this.</strong> Both frameworks are computed
                        from the same <code>work_items</code> layer: Jira issues + MR merges + prod-deploy anchors for
                        mini-pods; bug-queue state transitions + the same anchors for AI-first. AI involvement is
                        detected from MR evidence, never from labels. A mini-pod is <strong>n=1</strong> (one
                        developer), so each pod is a health indicator against its own pinned baseline — never mini-pod
                        vs mini-pod on a short window, never a leaderboard. The gate tile&apos;s turnaround is pooled
                        across pods, so it stays an approximation (marked ≈; medians don&apos;t sum) — the per-pod gate
                        table below shows each pod&apos;s exact human/agent review medians.
                    </p>
                </>
            )}
        </div>
    )
}
